import { useToast } from "@/components/ui/use-toast";
import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useInvalidateDiaries from "../../diaries/features/edit/api/invalidateDiaries";
import { consentKeys } from "./selectConsentById";

interface UnsignConsentParams {
	consent_id: string;
	user_id: string;
	diary_id?: string;
}

/**
 * Removes a user's signature from a consent form
 */
async function unsignConsent(
	params: UnsignConsentParams,
): Promise<{ success: boolean }> {
	const { consent_id, user_id } = params;

	if (!consent_id || !user_id) {
		throw new Error(
			"Missing required fields: consent_id and user_id are required",
		);
	}

	// Either delete the signature record or set signed_at to null
	const { error } = await supabaseClient
		.from("consent_signatures")
		.update({ signed_at: null })
		.eq("consent_id", consent_id)
		.eq("user_id", user_id);

	if (error) {
		throw new Error(`Error removing signature: ${error.message}`);
	}

	return { success: true };
}

/**
 * Hook for managing consent signature removal
 */
export function useUnsignConsentMutation(options?: {
	onSuccess?: (
		data: { success: boolean },
		variables: UnsignConsentParams,
	) => void;
}) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const invalidateDiaries = useInvalidateDiaries();

	return useMutation({
		mutationFn: unsignConsent,
		onSuccess: (data, variables) => {
			// Invalidate the specific consent query to refresh the data
			if (variables.consent_id) {
				queryClient.invalidateQueries({
					queryKey: consentKeys.byId(variables.consent_id),
				});
			}

			// Invalidate the diary's consents if diary_id is provided
			if (variables.diary_id) {
				queryClient.invalidateQueries({
					queryKey: consentKeys.byDiaryId(variables.diary_id),
				});
			}

			// Show success toast
			toast({
				title: "Signature removed",
				description: "Your signature has been removed from this consent form",
			});

			// Call the onSuccess callback if provided
			if (options?.onSuccess) {
				options.onSuccess(data, variables);
			}

			// Invalidate diaries to refresh the consent status
			invalidateDiaries();
		},
		onError: (error) => {
			// Show error toast
			toast({
				title: "Error removing signature",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
		},
	});
}
